import { createServerFn } from "@tanstack/react-start";
import { redirect } from "@tanstack/react-router";
import { Result } from "better-result";
import { z } from "zod/v4";
import { m } from "#/paraglide/messages";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { translateApiError } from "#/modules/util.errors/translate";
import { getAuthSession } from "#/modules/util.auth/session.auth.server";
import { getUserInfo } from "#/modules/util.auth/api.auth.server";
import { loginCallback } from "#/modules/util.auth/authenticate.server";
import { getLocaleOnServer } from "#/modules/util.i18n/get-locale.server";
import { buildLocaleHref } from "#/modules/util.helpers/route-href";
import { toResult } from "#/modules/util.auth/api-errors";
import { passwordSchema } from "#/modules/util.validation/schema.password";
import { apiEnum } from "#/modules/util.helpers/zod-enum";
import { InviteType } from "#/schema.nswag";
import type { components } from "#/schema";

// ---------------------------------------------------------------------------
// Return types — must be fully serializable (no `unknown`)
// ---------------------------------------------------------------------------

/**
 * Returned by validateAndRouteInviteFn — tells the caller exactly which route
 * to redirect to so the loader can remain a pure function with no direct
 * session/h3 calls.
 */
export type InviteRouteDecision =
  | {
      action: "redirect-new";
      inviteId: string;
      token: string;
      schoolName: string;
      inviteeEmail: string;
      inviteType: components["schemas"]["InviteType"];
    }
  | {
      action: "redirect-existing";
      inviteId: string;
      token: string;
      schoolName: string;
      inviteeEmail: string;
    }
  | {
      action: "redirect-login";
      inviteId: string;
      token: string;
      inviteeEmail: string;
      schoolName: string;
    }
  | { action: "retry" }
  | { action: "error"; error: string };

// ---------------------------------------------------------------------------
// Server functions
// ---------------------------------------------------------------------------

/**
 * Validate + decide routing — combines the API call and session check so the
 * route loader never needs to call getAuthSession() directly.
 */
export const validateAndRouteInviteFn = createServerFn({ method: "GET" })
  .validator(
    z.object({
      inviteId: z.string().min(1),
      token: z.string().min(1),
    }),
  )
  .handler(async ({ data }): Promise<InviteRouteDecision> => {
    const validateResult = toResult(
      await client.GET("/auth/validate-invite", {
        params: { query: { inviteId: data.inviteId, token: data.token } },
      }),
    );

    if (Result.isError(validateResult)) {
      if (validateResult.error.title === "NotAuthorized") {
        // Logged-in user ≠ invite email: clear the session and restart the flow
        // unauthenticated instead of showing an error the visitor can't act on.
        const session = await getAuthSession();
        await session.update({ tokens: undefined, user: undefined });
        return { action: "retry" };
      }
      return {
        action: "error",
        error:
          validateResult.error.status === 404
            ? m["invite.errors.invite_not_found"]()
            : translateApiError(validateResult.error),
      };
    }
    const validateData = validateResult.value;

    // NOTE: the backend's ValidateInviteResponse returns the SchoolId in its `inviteId`
    // field (legacy contract — the reference portal app never reads it, it re-uses the
    // invite id from the request URL). So the authoritative invite id is `data.inviteId`,
    // NOT `validateData.inviteId`. Using the latter here previously routed /invite/new and
    // /invite/existing with the SchoolId, which the accept call then rejected as
    // InviteNotFound. Mirror the reference portal: carry the request's inviteId forward.
    const inviteId = data.inviteId;
    const { inviteType, inviteeEmail, schoolName, userExists } = validateData;

    if (userExists) {
      const session = await getAuthSession();
      const user = session.data.user;

      if (!user) {
        // Not logged in → send to login with invite context so they can authenticate first
        return { action: "redirect-login", inviteId, token: data.token, inviteeEmail, schoolName };
      }

      return { action: "redirect-existing", inviteId, token: data.token, schoolName, inviteeEmail };
    }

    // New user — clear any leftover session
    const session = await getAuthSession();
    await session.update({ tokens: undefined, user: undefined });

    return {
      action: "redirect-new",
      inviteId,
      token: data.token,
      schoolName,
      inviteeEmail,
      inviteType,
    };
  });

/**
 * POST — register a new user with the invite, then accept it, then log in.
 * On success throws a redirect (via loginCallback); returns an error object
 * on any intermediate failure so the form can display it.
 */
export const acceptInviteNewFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      inviteId: z.string().min(1),
      token: z.string().min(1),
      inviteeEmail: z.string().email(),
      inviteType: apiEnum(InviteType),
      firstName: z.string().min(1, { message: m["validation.first_name_required"]() }),
      lastName: z.string().min(1, { message: m["validation.last_name_required"]() }),
      password: passwordSchema,
    }),
  )
  .handler(async ({ data }): Promise<{ formError: string } | undefined> => {
    // Step 1: Register the user
    const registerResult = toResult(
      await client.POST("/auth/register", {
        body: {
          email: data.inviteeEmail,
          password: data.password,
          firstName: data.firstName,
          lastName: data.lastName,
          inviteType: data.inviteType,
        },
      }),
    );

    if (Result.isError(registerResult)) {
      if (registerResult.error.title === "RegistrationFailed") {
        return { formError: m["invite.errors.registration_failed"]() };
      }
      return { formError: m["common.something_went_wrong_email"]() };
    }
    const registerData = registerResult.value;

    // Step 2: Persist the session tokens + user so the authenticated accept call works
    const userResult = await getUserInfo(registerData.accessToken ?? "");
    if (Result.isError(userResult)) {
      return { formError: m["common.something_went_wrong_email"]() };
    }
    const session = await getAuthSession();
    await session.update({ tokens: registerData, user: userResult.value });

    // Step 3: Accept the invite (now authenticated)
    const acceptResult = toResult(
      await client.POST("/school/docenten/invites/accept", {
        body: {
          schooldocentInviteId: data.inviteId,
          token: data.token,
        },
      }),
    );

    if (Result.isError(acceptResult)) {
      return { formError: translateApiError(acceptResult.error) };
    }
    const acceptData = acceptResult.value;

    // Step 4: Store the target school slug as the post-login redirect, then redirect
    const locale = getLocaleOnServer();
    await session.update({
      authRedirect: buildLocaleHref(`/${acceptData.schoolSlug}`, { locale }),
    });
    await loginCallback(); // throws redirect — never returns
    return undefined;
  });

/**
 * POST — accept an existing user's invite.
 * The user must already be logged in (their session is populated).
 * On success it always throws a redirect to the accepted school (never returns a success value).
 */
export const acceptInviteExistingFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      inviteId: z.string().min(1),
      token: z.string().min(1),
    }),
  )
  .handler(async ({ data }): Promise<{ formError: string } | undefined> => {
    const acceptResult = toResult(
      await client.POST("/school/docenten/invites/accept", {
        body: {
          schooldocentInviteId: data.inviteId,
          token: data.token,
        },
      }),
    );

    if (Result.isError(acceptResult)) {
      return { formError: translateApiError(acceptResult.error) };
    }
    const acceptData = acceptResult.value;

    // Redirect to the school that was just accepted
    const locale = getLocaleOnServer();
    throw redirect({
      to: "/$locale/{-$schoolyear}/$school",
      params: { locale, school: acceptData.schoolSlug },
    });
  });
